import {
  GIFT_OPTIONS_STEP,
  LIST_ADDRESS_STEP,
  NEW_ADDRESS_STEP,
  NEW_BILLING_ADDRESS_STEP,
  PAYMENT_STEP,
  REVIEW_STEP,
  SELECT_BILLING_ADDRESS_STEP
} from 'constants/melodyCheckoutFlow';
import { DIGITAL_GC_ONLY_CART } from 'constants/cartTypes';
import { CART_RESET_STATE, CHECKOUT_UPDATED_QUANTITY, RECEIVE_CART_ITEMS, REDIRECT } from 'constants/reduxActions';
import { REDEEM_REWARDS_ERROR, REDEEM_REWARDS_SUCCESS, REDEEM_REWARDS_TIMEOUT } from 'constants/rewardsInfo';
import { pvMelodyOrderConfirmation } from 'events/checkout';

import { call, put, select, takeEvery } from 'redux-saga/effects';
import { buildAuthenticationRedirectUrl } from 'utils/redirect';
import { clearCartCount, updateCartCount } from 'actions/cart';
import { titaniteView, track } from 'apis/amethyst';
import { addToList, changeCart, configureCheckout, getAkitaEstimator, getOrdersByPurchaseId, getPixelData, getRedeemableRewards, placeOrder, recordCheckoutError } from 'apis/checkout';
import { trackOrderConfirmationEvent } from 'helpers/analytics';
import { buildOrderConfirmationEventData, isDigitalChallenge } from 'helpers/CheckoutUtils';
import logger from 'middleware/logger';
import {
  ERROR_CANNOT_CONFIRM_PURCHASE_OOS,
  ERROR_CANNOT_CONFIRM_PURCHASE_OTHER,
  ERROR_EMPTY_CART,
  ERROR_INVALID_GIFT_OPTIONS,
  ERROR_NOT_AUTHENTICATED,
  ERROR_PURCHASE_NOT_FOUND,
  ERROR_QUANTITY_CHANGE_REQUEST_VALIDATION,
  ERROR_REQUEST_VALIDATION,
  REDEEMABLE_REWARDS_NOT_FOUND
} from 'middleware/fetchErrorMiddleware';
import {
  configurePurchase,
  onAddOrEditAddressEvent,
  onAddOrEditBillingAddressErrorEvent,
  onAddOrEditShippingAddressErrorEvent,
  onAddPaymentInstrumentErrorEvent,
  onAddPaymentInstrumentEvent,
  onAsinHasGoneOos,
  onDetermineIsEnrolledInRewards,
  onRequestRedeemableRewards,
  requestPayments,
  resetCheckout
} from 'store/ducks/checkout/actions';
import { onToggleIsAlsoBilling, storeTempFailureMsg, storeTempSuccessMsg } from 'store/ducks/address/actions';
import {
  CHECKOUT_API_ERROR_NOT_AUTHORIZED,
  CHECKOUT_FETCH_AKITA_ESTIMATE,
  CHECKOUT_FETCH_ORDERS_BY_PURCHASE_ID,
  CHECKOUT_HIDE_GIFT_OPTIONS_MODAL_ON_CLICK,
  CHECKOUT_ON_ADD_NEW_BILLING_ADDRESS_FROM_MODAL_ADDRESS_LIST_CLICK,
  CHECKOUT_ON_ADD_NEW_SHIP_ADDRESS_FROM_MODAL_ADDRESS_LIST_CLICK,
  CHECKOUT_ON_ASIN_HAS_GONE_OOS,
  CHECKOUT_ON_HIDE_BILLING_ADDRESS_FORM_MODAL_FROM_MODAL_ADDRESS_LIST_CLICK,
  CHECKOUT_ON_HIDE_BILLING_ADDRESS_MODAL_CLICK,
  CHECKOUT_ON_HIDE_NEW_SHIPPING_ADDRESS_MODAL_CLICK,
  CHECKOUT_ON_HIDE_SHIPPING_ADDRESS_FORM_MODAL_FROM_MODAL_ADDRESS_LIST_CLICK,
  CHECKOUT_ON_MAX_STEP_IS_CART_STEP,
  CHECKOUT_ON_MOVE_TO_FAVORITES_CLICK,
  CHECKOUT_ON_SHOW_ADD_NEW_SHIPPING_ADDRESS_MODAL_CLICK,
  CHECKOUT_RECEIVE_ORDER_INFORMATION,
  CHECKOUT_REDEEM_REWARDS_POINTS,
  CHECKOUT_SEND_TO_DESIRED_PAGE,
  CHECKOUT_SEND_TO_GIFT_OPTIONS_MODAL_ON_CLICK,
  CHECKOUT_SEND_TO_MAX_AVAILABLE_STEP,
  CHECKOUT_SEND_TO_NEW_ADDRESS_WHEN_NO_SAVED_ADDRESSES,
  CHECKOUT_SET_CONFIRMATION_PIXEL_PAYLOAD,
  CHECKOUT_STORE_AKITA_ESTIMATE,
  CONFIGURE_CHECKOUT,
  CONFIGURE_CHECKOUT_SUCCESS,
  DELETE_CHECKOUT_ADDRESS,
  PLACE_ORDER,
  REQUEST_CHECKOUT_ADDRESSES,
  REQUEST_CHECKOUT_PAYMENTS,
  REQUEST_CHECKOUT_REDEEMABLE_REWARDS,
  REQUEST_CHECKOUT_SHIP_OPTIONS,
  SAVE_CHECKOUT_ADDRESS,
  SAVE_CHECKOUT_PAYMENT_METHOD,
  SET_CHECKOUT_DATA_LOADING,
  SET_CHECKOUT_REDEEMABLE_REWARDS,
  SET_FORM_ERRORS,
  SET_IS_PLACING_ORDER,
  VERIFY_CHECKOUT_CREDIT_CARD
} from 'store/ducks/checkout/types';
import { SET_REDEEMING_REWARDS_STATUS } from 'store/ducks/rewards/types';
import { SET_ADDRESS_DATA_LOADING } from 'store/ducks/address/types';
import { clearPaymentErrors, setHasVerifyCreditCardError } from 'store/ducks/payment/actions';
import { SET_PAYMENT_DATA_LOADING } from 'store/ducks/payment/types';
import { workDeleteAddress, workRequestAddresses, workUpdateAddress } from 'store/ducks/address/sagas';
import { workRedeemPoints } from 'store/ducks/rewards/sagas';
import {
  getAccountApiAndCredentials,
  getAddressFormItemIsBilling,
  getAkitaKey,
  getCartType,
  getCheckoutLinks,
  getCheckoutProducts,
  getFormattedConstraintViolations,
  getIsAlsoBilling,
  getIsPlacingOrder,
  getMafiaAndCredentials,
  getNumberPurchaseProducts,
  getPurchaseAddresses,
  getPurchaseCard,
  getPurchaseData,
  getPurchaseId,
  getPurchaseShippingAddress,
  getUseAsDefaults,
  getUsePromoBalances,
  getVersionNumber,
  getWasAddressValid
} from 'store/ducks/readFromStore';
import { workRequestPayments, workSavePaymentInstrument, workVerifyCreditCard } from 'store/ducks/payment/sagas';
import { withSession } from 'store/ducks/session/sagas';
import { workRequestShipOptionsForPurchaseId } from 'store/ducks/shipOption/sagas';
import { SET_SHIP_OPTION_DATA_LOADING } from 'store/ducks/shipOption/types';

export function* watchOnFetchOrdersByPurchaseId() {
  yield takeEvery(CHECKOUT_FETCH_ORDERS_BY_PURCHASE_ID, workFetchOrdersByPurchaseId);
}

export function* watchOnHideGiftOptions() {
  yield takeEvery(CHECKOUT_HIDE_GIFT_OPTIONS_MODAL_ON_CLICK, workGoToStep, { step: REVIEW_STEP });
}

export function* watchOnViewGiftOptions() {
  yield takeEvery(CHECKOUT_SEND_TO_GIFT_OPTIONS_MODAL_ON_CLICK, workGoToStep, { step: GIFT_OPTIONS_STEP });
}

export function* watchOnMoveToFavoritesClick() {
  yield takeEvery(CHECKOUT_ON_MOVE_TO_FAVORITES_CLICK, workOnMoveToFavoritesClick);
}

export function* watchSendToDesiredPage() {
  yield takeEvery(CHECKOUT_SEND_TO_DESIRED_PAGE, workGoToStep);
}

export function* watchSendToMaxAvailableStep() {
  yield takeEvery(CHECKOUT_SEND_TO_MAX_AVAILABLE_STEP, workGoToStep);
}

export function* watchOnHideBillingAddressFromModalAddressList() {
  yield takeEvery(CHECKOUT_ON_HIDE_BILLING_ADDRESS_FORM_MODAL_FROM_MODAL_ADDRESS_LIST_CLICK, workGoToStep, { step: SELECT_BILLING_ADDRESS_STEP });
}

export function* watchOnHideShippingAddressFromModalAddressList() {
  yield takeEvery(CHECKOUT_ON_HIDE_SHIPPING_ADDRESS_FORM_MODAL_FROM_MODAL_ADDRESS_LIST_CLICK, workGoToStep, { step: LIST_ADDRESS_STEP });
}

export function* watchOnAddNewBillingAddressFromModalAddressList() {
  yield takeEvery(CHECKOUT_ON_ADD_NEW_BILLING_ADDRESS_FROM_MODAL_ADDRESS_LIST_CLICK, workGoToStep, { step: NEW_BILLING_ADDRESS_STEP });
}

export function* watchOnAddNewShippingAddressFromModalAddressList() {
  yield takeEvery(CHECKOUT_ON_ADD_NEW_SHIP_ADDRESS_FROM_MODAL_ADDRESS_LIST_CLICK, workGoToStep, { step: NEW_ADDRESS_STEP });
}

export function* watchOnMaxStepIsCartStep() {
  yield takeEvery(CHECKOUT_ON_MAX_STEP_IS_CART_STEP, workGoToCart);
}

export function* watchOnSendToNewAddressWhenNoSavedAddresses() {
  yield takeEvery(CHECKOUT_SEND_TO_NEW_ADDRESS_WHEN_NO_SAVED_ADDRESSES, workGoToStep, { step: NEW_ADDRESS_STEP });
}

export function* watchOnShowNewShippingAddressModalClick() {
  yield takeEvery(CHECKOUT_ON_SHOW_ADD_NEW_SHIPPING_ADDRESS_MODAL_CLICK, workGoToStep, { step: NEW_ADDRESS_STEP });
}

export function* watchOnHideBillingAddressModalClick() {
  yield takeEvery(CHECKOUT_ON_HIDE_BILLING_ADDRESS_MODAL_CLICK, workGoToStep, { step: REVIEW_STEP });
}

export function* watchOnHideNewShippingAddressModalClick() {
  yield takeEvery(CHECKOUT_ON_HIDE_NEW_SHIPPING_ADDRESS_MODAL_CLICK, workGoToStep, { step: LIST_ADDRESS_STEP });
}

export function* watchOnFetchAkitaEstimate() {
  yield takeEvery(CHECKOUT_FETCH_AKITA_ESTIMATE, workOnFetchAkitaEstimate);
}

export function* workOnFetchAkitaEstimate() {
  const akitaKey = yield select(getAkitaKey);
  const { mafia, credentials } = yield select(getMafiaAndCredentials);
  try {
    const { payload } = yield call(withSession, [getAkitaEstimator, mafia, akitaKey, credentials]);
    yield put({ type: CHECKOUT_STORE_AKITA_ESTIMATE, payload });
  } catch (error) {
    yield call(catchHandler, error);
  }
}

export function* workFetchOrdersByPurchaseId({ pId }) {
  try {
    const { mafia, credentials } = yield select(getMafiaAndCredentials);
    const isPlacingOrder = yield select(getIsPlacingOrder);
    const { payload } = yield call(withSession, [getOrdersByPurchaseId, mafia, pId, credentials]);
    yield put({ type: CHECKOUT_RECEIVE_ORDER_INFORMATION, payload });

    if (isPlacingOrder) {
      yield call(workFetchPixelData, pId);
    }
  } catch (error) {
    yield call(catchHandler, error);
  }
}

export function* workFetchPixelData(pId) {
  try {
    const { mafia, credentials } = yield select(getMafiaAndCredentials);
    const { payload } = yield call(withSession, [getPixelData, mafia, pId, credentials]);
    yield put({ type: CHECKOUT_SET_CONFIRMATION_PIXEL_PAYLOAD, payload });
  } catch (error) {
    logger('Unable to get valid response from /pixel endpoint, not firing pixel server. Erro was: ', error);
  }
}

export function* workGoToStep(params) {
  const { step, query = {} } = params;
  let qs = '';
  Object.keys(query).forEach(key => {
    if (key !== 'pid') {
      qs += `&${key}=${query[key]}`;
    }
  });
  const links = yield select(getCheckoutLinks);
  const location = `${links[step]}${qs}`;
  yield put({ type: REDIRECT, location });
}

export function* workOnMoveToFavoritesClick({ payload: { asin, lineItemId } }) {
  try {
    const { mafia } = yield select(getMafiaAndCredentials);
    const { account, credentials } = yield select(getAccountApiAndCredentials);
    const numItems = yield select(getNumberPurchaseProducts);
    yield call(withSession, [addToList, account, { subItemId: asin }, credentials]);
    const { payload: cart } = yield call(withSession, [changeCart, mafia, { 'items': [{ asin, quantity: 0 }] }, credentials]);
    yield put({ type: RECEIVE_CART_ITEMS, cartCount: cart.activeItemTotalQuantity || 0, cartObj: cart });
    yield call(updateCartCount, cart);

    if (numItems > 1) {
      yield put(configurePurchase({ quantityUpdate: { lineItemId, quantity: 0 }, advanceOnSuccess: false, includeShipmentOptions: true }));
    } else {
      yield call(workGoToCart);
    }
  } catch (error) {
    const links = yield select(getCheckoutLinks);
    yield call(catchHandler, error, links[REVIEW_STEP]);
  }
}

export function* workGoToCart() {
  const location = '/cart';
  yield put({ type: REDIRECT, location });
}

export function* watchRequestCheckoutShipOptions() {
  yield takeEvery(REQUEST_CHECKOUT_SHIP_OPTIONS, workRequestCheckoutShipOptions);
}

export function* watchDeleteCheckoutAddress() {
  yield takeEvery(DELETE_CHECKOUT_ADDRESS, workDeleteCheckoutAddress);
}

export function* workDeleteCheckoutAddress({ addressId }) {
  try {
    const success = yield call(workDeleteAddress, addressId);
    const addresses = yield select(getPurchaseAddresses);
    if (addresses.length === 1) {
      yield put(configurePurchase({ advanceOnSuccess: true }));
    } else {
      yield call(workGoToStep, { step: LIST_ADDRESS_STEP });
    }

    if (success) {
      yield call(workRequestAddresses);
      yield put(storeTempSuccessMsg('Address was deleted!'));
    } else {
      yield put(storeTempFailureMsg('Unable to delete address at this time.'));
    }
  } catch (error) {
    const links = yield select(getCheckoutLinks);
    yield put(storeTempFailureMsg('Unable to delete address at this time.'));
    yield call(catchHandler, error, links[LIST_ADDRESS_STEP]);
  }
}

export function* workRequestCheckoutShipOptions() {
  try {
    yield call(workRequestShipOptionsForPurchaseId);
  } catch (error) {
    const links = yield select(getCheckoutLinks);
    yield put({ type: SET_SHIP_OPTION_DATA_LOADING, payload: false });
    yield call(catchHandler, error, links[LIST_ADDRESS_STEP]);
  }
}

export function* watchConfigureCheckout() {
  yield takeEvery(CONFIGURE_CHECKOUT, workConfigureCheckout);
}

export function* workConfigureCheckout(actionPayload) {
  const links = yield select(getCheckoutLinks);
  try {
    const { configureParams } = actionPayload;
    const { advanceOnSuccess, reqData } = configureParams;
    const usePromoBalances = yield select(getUsePromoBalances);
    configureParams.reqData.useGCBalance = usePromoBalances;
    configureParams.reqData.useDiscount = usePromoBalances;

    if (!reqData.purchaseId) {
      const purchaseId = yield select(getPurchaseId);
      configureParams.reqData.purchaseId = purchaseId;
    }

    if (reqData.quantityUpdate) {
      yield put({ type: CHECKOUT_UPDATED_QUANTITY });
      yield put({ type: CART_RESET_STATE });
    }

    const { mafia, credentials } = yield select(getMafiaAndCredentials);
    yield put({ type: SET_CHECKOUT_DATA_LOADING, payload: true });
    const { payload } = yield call(withSession, [configureCheckout, mafia, configureParams, credentials]);
    yield put({ type: CONFIGURE_CHECKOUT_SUCCESS, payload });
    if (advanceOnSuccess) {
      yield put({ type: REDIRECT, location: links[REVIEW_STEP] });
    }
  } catch (error) {
    yield put({ type: SET_CHECKOUT_DATA_LOADING, payload: false });
    yield call(catchHandler, error, links[LIST_ADDRESS_STEP]);
  }
}

export function* watchCheckoutRedeemRewardsPoints() {
  yield takeEvery(CHECKOUT_REDEEM_REWARDS_POINTS, workCheckoutRedeemRewardsPoints);
}

export function* workCheckoutRedeemRewardsPoints({ spendPoints }) {
  try {
    const result = yield call(workRedeemPoints, spendPoints);
    if (result) {
      yield put(onRequestRedeemableRewards());
      yield put(configurePurchase({}));
      yield put({ type: SET_REDEEMING_REWARDS_STATUS, redeemingRewardsStatus: REDEEM_REWARDS_SUCCESS });
    } else {
      yield put({ type: SET_REDEEMING_REWARDS_STATUS, redeemingRewardsStatus: REDEEM_REWARDS_TIMEOUT });
      yield put({ type: SET_CHECKOUT_DATA_LOADING, payload: false });
    }
  } catch (error) {
    // Based on where error happened, its possible the rewards have been applied if the error was
    // on checking the transaction.  Resetting the purchase will apply the reward in the event
    // it was actually redeemed.
    yield put(onRequestRedeemableRewards());
    yield put(configurePurchase({}));
    yield put({ type: SET_REDEEMING_REWARDS_STATUS, redeemingRewardsStatus: REDEEM_REWARDS_ERROR });
  }
}

export function* watchRequestCheckoutRedeemableRewards() {
  yield takeEvery(REQUEST_CHECKOUT_REDEEMABLE_REWARDS, workRequestCheckoutRedeemableRewards);
}

export function* workRequestCheckoutRedeemableRewards() {
  try {
    const akitaKey = yield select(getAkitaKey);
    const { mafia, credentials } = yield select(getMafiaAndCredentials);
    const { payload } = yield call(withSession, [getRedeemableRewards, mafia, akitaKey, credentials]);
    const { data: { redemption_increments: redeemableRewards, dollar_value: spendPointDollarValue, spend_points: spendPoints } } = payload;
    yield put({
      type: SET_CHECKOUT_REDEEMABLE_REWARDS,
      redeemableRewards,
      spendPointDollarValue,
      spendPoints
    });
    yield put(onDetermineIsEnrolledInRewards(true));
  } catch (error) {
    yield call(catchHandler, error);
  }
}

export function* watchRequestCheckoutAddresses() {
  yield takeEvery(REQUEST_CHECKOUT_ADDRESSES, workRequestCheckoutAddresses);
}

export function* workRequestCheckoutAddresses() {
  try {
    yield call(workRequestAddresses);
  } catch (error) {
    const links = yield select(getCheckoutLinks);
    yield put({ type: SET_ADDRESS_DATA_LOADING, payload: false });
    yield call(catchHandler, error, links[LIST_ADDRESS_STEP]);
  }
}

export function* watchVerifyCheckoutCreditCard() {
  yield takeEvery(VERIFY_CHECKOUT_CREDIT_CARD, workVerifyCheckoutCreditCard);
}

export function* workVerifyCheckoutCreditCard({ number, paymentInstrumentId }) {
  try {
    const addressId = yield select(getPurchaseShippingAddress);
    const cvs = yield select(getFormattedConstraintViolations);
    const purchaseId = yield select(getPurchaseId);
    const isADigitalChallenge = yield call(isDigitalChallenge, cvs);
    const purchaseIdOnDigitalChallenge = isADigitalChallenge ? purchaseId : null;
    const status = yield call(workVerifyCreditCard, { number, paymentInstrumentId, addressId, purchaseId: purchaseIdOnDigitalChallenge });

    if (status) {
      const paymentMethods = [
        {
          paymentInstrumentId,
          paymentMethodCode: 'CC'
        }
      ];
      yield put(setHasVerifyCreditCardError(false));
      yield put(configurePurchase({ paymentMethods, advanceOnSuccess: true, includePaymentsAndAddresses: true }));
    } else {
      yield put(setHasVerifyCreditCardError(true));
    }
    yield put(clearPaymentErrors());
  } catch (error) {
    const links = yield select(getCheckoutLinks);
    yield put(setHasVerifyCreditCardError(false));
    yield call(catchHandler, error, links[PAYMENT_STEP]);
  }
}

export function* watchUpdateCheckoutAddress() {
  yield takeEvery(SAVE_CHECKOUT_ADDRESS, workUpdateCheckoutAddress);
}

export function* workUpdateCheckoutAddress() {
  try {
    const isBilling = yield select(getAddressFormItemIsBilling);
    const addressId = yield call(workUpdateAddress);
    const wasValidAddress = yield select(getWasAddressValid);

    if (wasValidAddress) {
      if (isBilling) {
        const instrument = yield select(getPurchaseCard);
        yield call(workSaveCheckoutPaymentInstrument, { instrument, addressId });
      } else {
        yield put(configurePurchase({ addressId, advanceOnSuccess: true, includePaymentsAndAddresses: true }));
      }
      yield put(onAddOrEditAddressEvent(true, addressId, isBilling ? 1 : 2));
    }
  } catch (error) {
    const links = yield select(getCheckoutLinks);
    const isBilling = yield select(getAddressFormItemIsBilling);
    const step = isBilling ? SELECT_BILLING_ADDRESS_STEP : LIST_ADDRESS_STEP;
    yield put(isBilling ? onAddOrEditBillingAddressErrorEvent() : onAddOrEditShippingAddressErrorEvent());
    yield put({ type: SET_ADDRESS_DATA_LOADING, payload: false });
    yield call(catchHandler, error, links[step]);
  }
}

export function* watchRequestCheckoutPayments() {
  yield takeEvery(REQUEST_CHECKOUT_PAYMENTS, workRequestCheckoutPayments);
}

export function* workRequestCheckoutPayments() {
  try {
    const addressId = yield select(getPurchaseShippingAddress);
    yield call(workRequestPayments, addressId);
  } catch (error) {
    const links = yield select(getCheckoutLinks);
    yield put({ type: SET_PAYMENT_DATA_LOADING, payload: false });
    yield call(catchHandler, error, links[PAYMENT_STEP]);
  }
}

export function* watchSaveCheckoutPaymentInstrument() {
  yield takeEvery(SAVE_CHECKOUT_PAYMENT_METHOD, workSaveCheckoutPaymentInstrument);
}

export function* workSaveCheckoutPaymentInstrument({ instrument, addressId = 0, updatingAddress = false }) {
  const isAlsoBilling = yield select(getIsAlsoBilling);
  const shippingAddressId = yield select(getPurchaseShippingAddress);
  const addressIdToUse = isAlsoBilling ? shippingAddressId : addressId;
  const pid = yield select(getPurchaseId);
  const cartType = yield select(getCartType);
  const purchaseId = cartType === DIGITAL_GC_ONLY_CART ? pid : null;

  try {
    const paymentInstrumentId = yield call(workSavePaymentInstrument, { addressId: addressIdToUse, instrument, purchaseId });
    const paymentMethods = [
      {
        paymentInstrumentId: paymentInstrumentId,
        paymentMethodCode: 'CC'
      }
    ];
    const configureParams = {
      advanceOnSuccess: true,
      includePaymentsAndAddresses: true,
      reqData: {
        paymentMethods
      }
    };

    if (!updatingAddress) {
      yield put(onAddPaymentInstrumentEvent(paymentInstrumentId, instrument.isPrimary, true));
    }

    if (isAlsoBilling) {
      yield put(onToggleIsAlsoBilling(false));
    }

    yield call(workConfigureCheckout, { configureParams });
    yield put(requestPayments());
  } catch (error) {
    const links = yield select(getCheckoutLinks);
    if (!updatingAddress) {
      yield put(onAddPaymentInstrumentErrorEvent(instrument.isPrimary));
    }
    yield put({ type: SET_PAYMENT_DATA_LOADING, payload: false });
    yield call(catchHandler, error, links[PAYMENT_STEP]);
  }
}

export function* watchPlaceOrder() {
  yield takeEvery(PLACE_ORDER, workPlaceOrder);
}

export function* workPlaceOrder() {
  try {
    const purchaseId = yield select(getPurchaseId);
    const { mafia } = yield select(getMafiaAndCredentials);
    const versionNumber = yield select(getVersionNumber);
    const shouldDefaultOptions = yield select(getUseAsDefaults);
    const purchaseData = yield select(getPurchaseData);
    const { purchase: { chargeSummary, productList } } = purchaseData;
    yield put({ type: SET_IS_PLACING_ORDER, payload: true });
    const { payload } = yield call(placeOrder, mafia, purchaseId, versionNumber, shouldDefaultOptions);
    const { orderId, digitalOrderId } = payload;
    const orderConfirmationEventData = yield call(buildOrderConfirmationEventData, { chargeSummary, digitalOrderId, orderId, productList, purchaseId });
    yield call(trackOrderConfirmationEvent, orderConfirmationEventData);
    yield call(titaniteView);
    yield call(track, pvMelodyOrderConfirmation, { purchaseData, digitalOrderId, orderId });
    yield call(clearCartCount);
    yield put({ type: CART_RESET_STATE });
    yield put({ type: REDIRECT, location: `/mconfirmation/${purchaseId}` });
  } catch (error) {
    const links = yield select(getCheckoutLinks);
    yield put({ type: SET_IS_PLACING_ORDER, payload: false });
    yield call(catchHandler, error, links[REVIEW_STEP]);
  }
}

export function* watchNotifyOutOfStockAsin() {
  yield takeEvery(CHECKOUT_ON_ASIN_HAS_GONE_OOS, workNotifyOutOfStockAsin);
}

export function* workNotifyOutOfStockAsin({ asin }) {
  const products = yield select(getCheckoutProducts);
  const { productName } = (products || []).find(item => item.asin === asin) || {};
  if (productName) {
    alert(`Sorry, an item has gone out of stock and will be removed from the purchase: ${productName}`);
  }
  yield put(resetCheckout());
  yield put(configurePurchase({}));
  yield put({ type: REDIRECT, location: '/checkout' });
}

export function* catchHandler(e, redirectPath = '/mcheckout/spc') {
  switch (e.id) {
    case REDEEMABLE_REWARDS_NOT_FOUND: {
      yield put(onDetermineIsEnrolledInRewards(false));
      return;
    }
    case ERROR_CANNOT_CONFIRM_PURCHASE_OOS: {
      const { asin } = e.extraInformation[0];
      yield put(onAsinHasGoneOos(asin));
      return;
    }
    case ERROR_CANNOT_CONFIRM_PURCHASE_OTHER: {
      alert('Sorry, an error has occurred.  Please try placing your order again.');
      yield put(resetCheckout());
      yield put(configurePurchase({}));
      yield put({ type: REDIRECT, location: '/checkout' });
      return;
    }
    case ERROR_NOT_AUTHENTICATED:
      yield put({ type: CHECKOUT_API_ERROR_NOT_AUTHORIZED });
      yield put({ type: REDIRECT, location: buildAuthenticationRedirectUrl(redirectPath) });
      break;
    case ERROR_PURCHASE_NOT_FOUND:
    case ERROR_EMPTY_CART:
      yield put({ type: REDIRECT, location: '/cart' });
      break;
    case ERROR_REQUEST_VALIDATION:
      yield put({ type: SET_FORM_ERRORS, payload: e.extraInformation });
      break;
    case ERROR_INVALID_GIFT_OPTIONS:
      logger('TODO: invalid gift options error');
      break;
    case ERROR_QUANTITY_CHANGE_REQUEST_VALIDATION:
      logger('TODO: support quantity change error');
      break;
    default:
      logger('default error in catch: ', e);
      const qs = encodeURI(`type=martyCheckoutError&type=generic&error=${JSON.stringify(e)}`);
      recordCheckoutError(qs);
      return;
  }
}

export default [
  watchCheckoutRedeemRewardsPoints,
  watchConfigureCheckout,
  watchDeleteCheckoutAddress,
  watchOnFetchOrdersByPurchaseId,
  watchNotifyOutOfStockAsin,
  watchOnAddNewBillingAddressFromModalAddressList,
  watchOnAddNewShippingAddressFromModalAddressList,
  watchOnFetchAkitaEstimate,
  watchOnHideBillingAddressFromModalAddressList,
  watchOnHideBillingAddressModalClick,
  watchOnHideGiftOptions,
  watchOnHideShippingAddressFromModalAddressList,
  watchOnHideNewShippingAddressModalClick,
  watchOnMaxStepIsCartStep,
  watchOnMoveToFavoritesClick,
  watchOnSendToNewAddressWhenNoSavedAddresses,
  watchOnShowNewShippingAddressModalClick,
  watchOnViewGiftOptions,
  watchPlaceOrder,
  watchRequestCheckoutAddresses,
  watchRequestCheckoutPayments,
  watchRequestCheckoutRedeemableRewards,
  watchRequestCheckoutShipOptions,
  watchSaveCheckoutPaymentInstrument,
  watchSendToDesiredPage,
  watchSendToMaxAvailableStep,
  watchUpdateCheckoutAddress,
  watchVerifyCheckoutCreditCard
];
